import { Alert, Row, Spin } from 'antd'
import {
  DrilldownDataContext,
  useBufferedFilteredEntries,
} from 'features/drilldown'
import { PointCloudView } from 'features/pointClouds'
import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoPotreePointCloud, GenericPanelData } from 'types'

const LATEST_DATA_SCHEMA_VERSION = 1

export interface PointCloudPanelData extends GenericPanelData {
  panelDataSchemaVersion: number
  title: string
  isPinned: boolean
  selectedPointCloudIds: string[]
}

export const DEFAULT_POINT_CLOUD_PANEL_DATA: PointCloudPanelData = {
  panelDataSchemaVersion: LATEST_DATA_SCHEMA_VERSION,
  title: 'Point Cloud',
  isPinned: true,
  selectedPointCloudIds: [],
}

const AppContainer = styled.div<{ $isPresentationModeEnabled: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  height: calc(
    100vh - ${(props) => (props.$isPresentationModeEnabled ? '140px' : '210px')}
  );
  overflow: hidden;
`

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`

export const RawPointCloud: React.FC = () => {
  const { isPresentationModeEnabled } = useContext(DrilldownDataContext)

  // Load sites
  const { data: sites, isLoading: isLoadingSites } = useBufferedFilteredEntries(
    {
      entryType: 'site',
      limit: 2,
      dataKey: 'sites',
    }
  )

  // Load point clouds
  const { data: pointClouds, isLoading: isLoadingPointClouds } =
    useBufferedFilteredEntries({
      entryType: 'potree-point-cloud',
      dataKey: 'potreePointClouds',
    })

  const pointCloudIds = useMemo(
    () => pointClouds.map((pc: GeckoPotreePointCloud) => pc.id),
    [pointClouds]
  )

  if (isLoadingSites && sites.length === 0) {
    return (
      <Row justify='center' align='middle' style={{ height: '50px' }}>
        <Spin />
      </Row>
    )
  }

  if (sites.length !== 1) {
    return (
      <Alert
        title={`There needs to be exactly one site in scope to show a point cloud view. Current sites in scope: ${sites.length}`}
        type='info'
        showIcon
      />
    )
  }

  const [site] = sites

  if (pointCloudIds.length === 0) {
    return (
      <Alert
        title='There needs to be at least one point cloud for the current site to render a point cloud view.'
        type='info'
        showIcon
      />
    )
  }

  return (
    <AppContainer $isPresentationModeEnabled={isPresentationModeEnabled}>
      <PointCloudView siteID={site.id} pointCloudIDs={pointCloudIds} />
      {(isLoadingSites || isLoadingPointClouds) && (
        <LoadingOverlay>
          <Spin />
        </LoadingOverlay>
      )}
    </AppContainer>
  )
}

export const PointCloud = React.memo(RawPointCloud)
